import { useCallback, useEffect, useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { partnerNetworksApi, supplyAnalyticsApi, type ItemExternalRef } from '@/api/services';

/**
 * What this item is called in somebody else's system.
 *
 * The screens this came from showed "SAP Number" and "JDE Number" as two fields,
 * which cannot express the case they exist for: we run JDE, the customer runs
 * SAP, and the item has a number in both. One is our reference and one is
 * theirs, and an integration has to know which before it can quote either back.
 *
 * So the system is a value and the owner is a choice — "ours" or a company we
 * actually trade with.
 */

const OURS = '__ours__';
const SYSTEMS = ['SAP', 'JDE', 'ORACLE', 'MAXIMO'];

export function ExternalRefsCard({ itemId }: { itemId: string }) {
  const [refs, setRefs] = useState<ItemExternalRef[]>([]);
  const [partners, setPartners] = useState<{ id: string; name: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [system, setSystem] = useState('SAP');
  const [reference, setReference] = useState('');
  const [owner, setOwner] = useState(OURS);

  const load = useCallback(async () => {
    try {
      setRefs(await supplyAnalyticsApi.refs(itemId));
    } catch {
      toast.error('Could not load external references');
    } finally {
      setLoading(false);
    }
  }, [itemId]);

  useEffect(() => {
    void load();
    partnerNetworksApi
      .getAll()
      .then((res) =>
        setPartners(
          (res.asOwner ?? [])
            .filter((p) => p.status === 'ACTIVE')
            .map((p) => ({
              id: p.partnerCompanyId,
              name: p.partnerCompany?.displayName ?? p.partnerCompany?.name ?? p.partnerCompanyId,
            }))
        )
      )
      .catch(() => setPartners([]));
  }, [load]);

  const add = async () => {
    if (!reference.trim()) return;
    setAdding(true);
    try {
      await supplyAnalyticsApi.setRef(itemId, {
        system,
        reference: reference.trim(),
        ownerCompanyId: owner === OURS ? undefined : owner,
      });
      toast.success(`${system} reference recorded`);
      setReference('');
      await load();
    } catch (error) {
      const message =
        (error as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        'Could not record that reference';
      toast.error(message);
    } finally {
      setAdding(false);
    }
  };

  const remove = async (ref: ItemExternalRef) => {
    try {
      await supplyAnalyticsApi.removeRef(itemId, ref.id);
      toast.success(`${ref.system} reference removed`);
      await load();
    } catch {
      toast.error('Could not remove that reference');
    }
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">External references</CardTitle>
        <CardDescription>
          What this item is called elsewhere, both in the operator&rsquo;s own ERP and in a customer&rsquo;s.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : refs.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            None recorded. Without one, an order arriving under another party&rsquo;s part number
            has to be matched by hand.
          </p>
        ) : (
          <ul className="divide-y">
            {refs.map((ref) => (
              <li key={ref.id} className="flex items-center justify-between gap-3 py-2 first:pt-0">
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="font-mono text-[11px]">
                    {ref.system}
                  </Badge>
                  <span className="font-mono text-sm">{ref.reference}</span>
                  <span className="text-xs text-muted-foreground">
                    {/* Whose system, always said. A number with no owner is
                        ambiguous in exactly the way that breaks an integration. */}
                    {ref.ownerCompany ? `${ref.ownerCompany.displayName}'s` : 'ours'}
                  </span>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={`Remove ${ref.system} reference`}
                  onClick={() => remove(ref)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </li>
            ))}
          </ul>
        )}

        <div className="flex flex-wrap items-end gap-2 border-t pt-3">
          <Select value={system} onValueChange={setSystem}>
            <SelectTrigger className="w-28">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SYSTEMS.map((s) => (
                <SelectItem key={s} value={s}>
                  {s}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Input
            className="w-44"
            placeholder="Reference"
            value={reference}
            onChange={(e) => setReference(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && add()}
          />

          <Select value={owner} onValueChange={setOwner}>
            <SelectTrigger className="w-48">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={OURS}>Ours</SelectItem>
              {partners.map((p) => (
                <SelectItem key={p.id} value={p.id}>
                  {p.name}&rsquo;s
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Button size="sm" onClick={add} disabled={adding || !reference.trim()}>
            <Plus className="mr-1.5 h-3.5 w-3.5" />
            Add
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
